import * as React from 'react';
import { Header, Page, Content } from '@backstage/core-components';
import {
  useApi,
  fetchApiRef,
  discoveryApiRef,
} from '@backstage/core-plugin-api';
import { Tabs, Tab, Box, Button } from '@material-ui/core';
import CompareArrowsIcon from '@material-ui/icons/CompareArrows';
import { DevxpClient } from '../api';
import { AnalyticsDashboard } from './AnalyticsDashboard';
import { DashboardContent } from './DashboardContent';
import { SettingsContent } from './SettingsContent';
import { DeveloperLeaderboard } from './DeveloperLeaderboard';
import { DeveloperDetails } from './DeveloperDetails';
import { DeveloperComparison } from './DeveloperComparison';
import { RepositoriesContent } from './RepositoriesContent';

type DevView = 'leaderboard' | 'details' | 'comparison';

export const DevxpPage = () => {
  const [tab, setTab] = React.useState(0);
  const fetchApi = useApi(fetchApiRef);
  const discoveryApi = useApi(discoveryApiRef);

  const api = React.useMemo(
    () => new DevxpClient({ fetchApi, discoveryApi }),
    [fetchApi, discoveryApi],
  );

  // Pre-load repo names so the Developers filter is populated before the user visits that tab
  const [repoOptions, setRepoOptions] = React.useState<string[]>([]);
  React.useEffect(() => {
    api
      .getRepositories({ pageSize: 200 })
      .then(data => setRepoOptions(data.repositories.map(r => r.repoName)))
      .catch(() => {});
  }, [api]);

  // Developers tab state
  const [devView, setDevView] = React.useState<DevView>('leaderboard');
  const [selectedUserId, setSelectedUserId] = React.useState('');
  const [compareList, setCompareList] = React.useState<string[]>([]);

  const handleViewDeveloper = (userId: string) => {
    setSelectedUserId(userId);
    setDevView('details');
  };

  const handleToggleCompare = (userId: string) => {
    setCompareList(prev =>
      prev.includes(userId) ? prev.filter(id => id !== userId) : [...prev.slice(-1), userId],
    );
  };

  const handleOpenComparison = () => {
    setDevView('comparison');
  };

  const handleBackToLeaderboard = () => {
    setDevView('leaderboard');
  };

  return (
    <Page themeId="tool">
      <Header
        title="Developer Intelligence"
        subtitle="DevXP Analytics & Developer Name Mapping"
      />
      <Content>
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          indicatorColor="primary"
          textColor="primary"
        >
          <Tab label="Analytics" />
          <Tab label="Developers" />
          <Tab label="Repositories" />
          <Tab label="Identity" />
          <Tab label="Settings" />
        </Tabs>
        <Box mt={3}>
          {tab === 0 && <AnalyticsDashboard api={api} />}

          {tab === 1 && (
            <Box>
              {devView === 'leaderboard' && (
                <>
                  {compareList.length > 0 && (
                    <Box mb={2} display="flex" justifyContent="flex-end">
                      <Button
                        variant="outlined"
                        color="primary"
                        size="small"
                        startIcon={<CompareArrowsIcon />}
                        onClick={handleOpenComparison}
                      >
                        Compare ({compareList.length})
                      </Button>
                    </Box>
                  )}
                  <DeveloperLeaderboard
                    api={api}
                    onViewDeveloper={handleViewDeveloper}
                    onCompareDeveloper={handleToggleCompare}
                    compareList={compareList}
                    repoOptions={repoOptions}
                  />
                </>
              )}

              {devView === 'details' && (
                <DeveloperDetails
                  api={api}
                  userId={selectedUserId}
                  onBack={handleBackToLeaderboard}
                  onCompare={handleToggleCompare}
                  isInCompare={compareList.includes(selectedUserId)}
                />
              )}

              {devView === 'comparison' && (
                <DeveloperComparison
                  api={api}
                  initialUserIds={compareList}
                  onBack={handleBackToLeaderboard}
                />
              )}
            </Box>
          )}

          {tab === 2 && (
            <RepositoriesContent
              api={api}
              onViewDeveloper={userId => {
                handleViewDeveloper(userId);
                setTab(1);
              }}
            />
          )}
          {tab === 3 && <DashboardContent api={api} />}
          {tab === 4 && <SettingsContent api={api} />}
        </Box>
      </Content>
    </Page>
  );
};
